import React from 'react';
import { Skeleton, SkeletonButton, SkeletonRegion, SkeletonStats } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';

import { STACK } from '../../constants/density';
import { useTranslation } from '../../i18n';
import useResponsive from '../../hooks/useResponsive';
import { MobileScreen, MobileHeader, MobileCardSkeleton } from '../../mobile';
import {
  MobileSectionStrip, RailSkeleton, SetupSurface, TableSurface,
  WORKBENCH_HEADER, WORKBENCH_MAIN, WORKBENCH_ROOT,
} from '../../components/common/WorkbenchSkeleton';

/**
 * «کالاها و خدمات» while it is still fetching — the page's own shape, in
 * placeholder blocks, drawn for a `view` (DESIGN.md §"Loading & skeleton").
 *
 * The router's Suspense fallback imports this EAGERLY, and the screen uses the
 * same component for its first load, so the fallback → first-load transition
 * does not jump. That is why it imports nothing from the screen: only the kit,
 * the tokens, the density scale, i18n, the mobile primitives and the shared
 * workbench geometry.
 *
 * The desktop anatomy it mirrors (Goods.web.jsx):
 *
 *   ① header      title · subtitle | primary action  — and NO window picker,
 *                 because nothing on this page is windowed
 *   ② goods strip three KPI cards, no icon slot
 *   ③ rail        176px, two groups — three daily entries and up to eight
 *                 definitions, five of which only a clinic's tier reveals
 *   ④ surface     the view's toolbar + its dense table (کالاها، خدمات، تجهیزات),
 *                 or a heading + add-form + bordered rows (the setup views and
 *                 the lifecycle screens — those are NOT tables, see RowSkeleton)
 *
 * **Three KPI cards, not four.** The strip drops a card for a figure this reader
 * cannot fetch, so a four-block placeholder followed by a three-card strip would
 * be exactly the jump a skeleton exists to prevent. Three is what everyone who
 * can open the page sees at minimum, and a fourth arriving is a smaller lie than
 * a fourth disappearing.
 *
 * **The rail is drawn at its «ساده» size**, for the same reason the screen
 * assumes «ساده» until the settings land: adding entries is far better than
 * flashing five that then vanish.
 */

const TABLE_VIEWS = {
  // Column widths mirror each view's `columns` definition; the first column is
  // the flexible one and gets the name-cell block.
  items: { columns: [160, 100, 160, 120, 120, 110, 110], rows: 8, filters: { search: 220, select: 150, chips: 3 } },
  services: { columns: [180, 120, 120, 130, 84, 110, 92], rows: 6, filters: { search: 240, select: 160, chips: 0 } },
  // EquipmentView's own `columns`; the six chips are the status filters.
  equipment: { columns: [84, 200, 120, 130, 170, 130, 96], rows: 8, filters: { search: 220, select: 150, chips: 6 } },
};

const SETUP_VIEWS = {
  suppliers: { maxWidth: 720, form: [{ flex: 1 }, { width: 170 }], rows: 3, actions: 3 },
  equipmentTypes: { maxWidth: 860, form: [{ flex: 1 }, { width: 150 }, { width: 150 }], rows: 3, actions: 2 },
  insurance: { maxWidth: 860, form: [{ flex: 1 }, { width: 130 }, { width: 160 }], rows: 3, actions: 2 },
  // The five tier-gated lifecycle screens. Each is a heading over a document
  // list rather than a table, so they draw the setup shape at their own width.
  expiry: { maxWidth: 900, form: null, rows: 4, actions: 2 },
  procurement: { maxWidth: 900, form: null, rows: 4, actions: 2 },
  custody: { maxWidth: 900, form: null, rows: 3, actions: 2 },
  counts: { maxWidth: 860, form: null, rows: 3, actions: 2 },
  locations: { maxWidth: 720, form: [{ flex: 1 }, { width: 150 }], rows: 3, actions: 2 },
};

export function resolveSkeletonView(view) {
  return TABLE_VIEWS[view] || SETUP_VIEWS[view] ? view : 'services';
}

/** «بیمه» is its own namespace; every other view names itself in `operations`. */
function viewTitleKey(view) {
  return view === 'insurance' ? 'insurance.title' : `operations.views.${view}`;
}

// ── Desktop ──────────────────────────────────────────────────────────────────

/** The object rail's footprint at «ساده»: three daily entries, two definitions. */
export function GoodsRailSkeleton() {
  return <RailSkeleton groups={[3, 2]} />;
}

export function GoodsSkeletonWeb({ view: requested = 'services' }) {
  const { t } = useTranslation();
  const view = resolveSkeletonView(requested);
  const label = t('common.state.loading');
  return (
    <SkeletonRegion
      label={label}
      data-testid="goods-skeleton"
      data-view={view}
      style={WORKBENCH_ROOT}
    >
      {/* ① — no window picker: nothing here is windowed */}
      <header style={WORKBENCH_HEADER}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.textPrimary }}>{t('nav.items.goods')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: C.textMuted }}>{t('goods.subtitle')}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {TABLE_VIEWS[view] ? <SkeletonButton width={128} /> : null}
        </div>
      </header>

      {/* ② three KPI cards, no icon slot */}
      <div style={{ padding: '14px 20px', flexShrink: 0 }}>
        <SkeletonStats count={3} icon={false} label={label} />
      </div>

      {/* ③ + ④ */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, borderTop: `1px solid ${C.border}` }}>
        <GoodsRailSkeleton />
        <main style={WORKBENCH_MAIN}>
          {TABLE_VIEWS[view]
            ? <TableSurface spec={TABLE_VIEWS[view]} label={label} />
            : <SetupSurface spec={SETUP_VIEWS[view]} title={t(viewTitleKey(view))} label={label} />}
        </main>
      </div>
    </SkeletonRegion>
  );
}

// ── Mobile ───────────────────────────────────────────────────────────────────

/**
 * The placeholder for ONE card of each mobile list, shaped like the card the
 * list renders (Goods.mobile.jsx → src/mobile/cards). Exported so the screen's
 * `MobileList`s pass the same shapes as the page skeleton draws.
 */
export const MOBILE_CARD_SKELETONS = {
  // MobileItemCard: name + state badge, the code, two fact rows, and (for a
  // keeper) the adjust shelf.
  items: ({ actions = 0 } = {}) => <MobileCardSkeleton badge={56} metaLines={3} actions={actions} />,
  // MobileServiceCard: name + tariff, one meta line, up to two chips.
  services: () => <MobileCardSkeleton badge={64} metaLines={1} chips={2} />,
  // MobileEquipmentCard: name + status badge, the code and room, two fact rows.
  equipment: () => <MobileCardSkeleton badge={64} metaLines={3} />,
  // The setup lists: a name, one meta line, and the edit/toggle shelf.
  suppliers: () => <MobileCardSkeleton badge={48} metaLines={1} actions={2} />,
  equipmentTypes: () => <MobileCardSkeleton badge={56} metaLines={2} actions={2} />,
  locations: () => <MobileCardSkeleton badge={48} metaLines={1} />,
  insurance: () => <MobileCardSkeleton badge={56} metaLines={1} actions={2} />,
  // The lifecycle documents: a status badge and two or three fact rows.
  expiry: () => <MobileCardSkeleton badge={64} metaLines={3} actions={1} />,
  procurement: () => <MobileCardSkeleton badge={56} metaLines={3} />,
  custody: () => <MobileCardSkeleton badge={56} metaLines={1} actions={2} />,
  counts: () => <MobileCardSkeleton badge={56} metaLines={3} actions={1} />,
};

/** `MobileList`'s `renderSkeleton` for a view: `renderGoodsCardSkeleton('items', { actions: 1 })`. */
export const renderGoodsCardSkeleton = (view, opts) => (i) => (
  <React.Fragment key={i}>{(MOBILE_CARD_SKELETONS[view] || MOBILE_CARD_SKELETONS.items)(opts)}</React.Fragment>
);

/**
 * The phone carries every destination the rail does, so the skeleton has a card
 * shape for each — the fallback to «کالاها» would otherwise draw a two-fact
 * stock card in front of a supplier list and jump when the data landed.
 */
const MOBILE_VIEWS = new Set(Object.keys(MOBILE_CARD_SKELETONS));

export function GoodsSkeletonMobile({ view: requested = 'services', actions = 0 }) {
  const { t } = useTranslation();
  const view = MOBILE_VIEWS.has(requested) ? requested : 'services';
  const label = t('common.state.loading');
  const card = MOBILE_CARD_SKELETONS[view];
  return (
    <SkeletonRegion
      label={label}
      data-testid="goods-skeleton"
      data-view={view}
      style={{ height: '100%' }}
    >
      <MobileScreen
        header={<MobileHeader title={t('nav.items.goods')} subtitle={t('goods.mobileTitle')} sticky />}
      >
        <MobileSectionStrip widths={[52, 48, 60]} />

        {/* ② the money rows — the shelf's value and what needs reordering */}
        <div style={{ padding: '0.75rem 1rem 0', display: 'grid', gap: '0.5rem' }}>
          <MobileCardSkeleton metaLines={0} badge={88} />
          <MobileCardSkeleton metaLines={0} badge={88} />
        </div>

        {/* the filter strip — the shelf and the issue queue have one, the
            tariff list and the setup lists do not */}
        {view === 'items' || view === 'custody' ? (
          <div aria-hidden style={{ display: 'flex', gap: '0.5rem', paddingBlock: '0.5rem', paddingInline: '1rem', overflow: 'hidden' }}>
            {[56, 96, 80, 72].map((w, i) => <Skeleton key={i} width={w} height={44} radius={RADIUS.pill} />)}
          </div>
        ) : null}

        <div style={{ padding: view === 'items' || view === 'custody' ? '0 1rem 1.5rem' : '0.75rem 1rem 1.5rem', display: 'grid', gap: `${STACK.tight}px` }}>
          {Array.from({ length: 5 }, (_, i) => <React.Fragment key={i}>{card({ actions })}</React.Fragment>)}
        </div>
      </MobileScreen>
    </SkeletonRegion>
  );
}

export default function GoodsSkeleton(props) {
  const { isMobile } = useResponsive();
  return isMobile ? <GoodsSkeletonMobile {...props} /> : <GoodsSkeletonWeb {...props} />;
}
